import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { api, type AppReadme } from '../api';
import { Alert, Button } from '../ds';
import { icons } from '../icons';
import { cardStyle } from '../tokens';

interface ReadmePanelProps {
  repo: string;
}

function readmeUrl(href: string | undefined, baseUrl: string): string | undefined {
  if (!href || href.startsWith('#')) return href;
  try {
    const resolved = new URL(href, baseUrl);
    return ['https:', 'http:', 'mailto:'].includes(resolved.protocol) ? resolved.toString() : undefined;
  } catch {
    return undefined;
  }
}

export function ReadmePanel({ repo }: ReadmePanelProps) {
  const [readme, setReadme] = useState<AppReadme | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.readme(repo)
      .then(({ readme: nextReadme }) => {
        if (!cancelled) setReadme(nextReadme);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load this README.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [repo]);

  if (loading) {
    return (
      <div className="readme-state" aria-live="polite">
        Loading README...
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger" showIcon>{error}</Alert>;
  }

  if (!readme) {
    return (
      <div className="readme-state">
        <strong>No README found</strong>
        <span>Add a README to the repository default branch to publish app documentation here.</span>
      </div>
    );
  }

  return (
    <section className="readme-panel" style={cardStyle}>
      <div className="readme-toolbar">
        <span>{readme.path}</span>
        <Button
          variant="outlined"
          size="sm"
          leftIcon={icons.openInNew}
          onPress={() => window.open(readme.htmlUrl, '_blank', 'noreferrer')}
        >
          View on GitHub
        </Button>
      </div>
      <article className="readme-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children, ...props }) => (
              <a
                {...props}
                href={readmeUrl(href, readme.htmlUrl)}
                target={href?.startsWith('#') ? undefined : '_blank'}
                rel={href?.startsWith('#') ? undefined : 'noreferrer'}
              >
                {children}
              </a>
            ),
            img: ({ src, alt, ...props }) => (
              <img {...props} src={readmeUrl(src, readme.htmlUrl)} alt={alt ?? ''} loading="lazy" />
            ),
          }}
        >
          {readme.content}
        </ReactMarkdown>
      </article>
    </section>
  );
}
